/**
 * §03.2 movement model, §03.4 dash, §03.6 dash buffering, §03.7 mercy + player knockback,
 * §09.5 Lumensurge meter (spine §2). Step 2 of the sim order.
 *
 * Determinism: reads only World state and the config registry. Consumes no RNG stream.
 */

import { STEP } from '../core/loop';
import { cfg } from '../core/config';
import type { World } from './world';
import { wrapPos } from './wrap';
import { statusSlowFactor, statusStunned } from './status';

/** §09.5.1 meter thresholds; index i is the meter required to reach tier i+1. */
export const SURGE_THRESHOLDS: readonly number[] = [0, 15, 35, 60, 90, 125, 165, 210, 260, 315];
export const SURGE_METER_CAP = 400;
export const SURGE_DECAY_GRACE_S = 3.0;
export const SURGE_HYSTERESIS = 5;

/**
 * §09.5.2 decay rate, in meter units per second at tier `t`: `BASE + PER_TIER × (t − 1)`.
 *
 * **AMD-46 — why this is a line through the origin and not `max(1, 0.5 t)`.** The old rule
 * charged a flat 1.0/s at tiers 1 and 2, which made the bottom of the ladder the most
 * expensive rung on it: tier 1 pays a ×1 multiplier — nothing — and billed the same upkeep
 * as tier 2. That is a cliff, and it was the last bistability in the economy. Below ~1
 * mote/s of income the meter could never reach the 15 needed for tier 2 however long you
 * played, so the damage and XP multipliers were not merely small, they were unreachable;
 * above 1/s they compounded. Measured, a §9.13 stander earned 0.20 mote/s and sat at a mean
 * tier of 1.02 for its whole run, against 6.75 for a patrolling pilot — a ×6.6 swing on both
 * damage and XP decided by which side of one constant your income landed.
 *
 * The line is deliberately the old `0.5 × tier` with the floor deleted and a flat 0.4/s
 * taken off: at tier 8 that is a 10% discount and a high-income pilot's equilibrium barely
 * moves, while tier 1 falls from 1.0/s to 0.1/s. Every income now buys *some* tier, and which
 * tier is a smooth function of how much you earn rather than a step at one constant.
 */
export const SURGE_DECAY_BASE = 0.1;
export const SURGE_DECAY_PER_TIER = 0.5;

/** Meter units bled per second at `tier`, once the §09.5.2 grace window has elapsed. */
export function surgeDecayRate(tier: number): number {
  const t = tier < 1 ? 1 : tier;
  return SURGE_DECAY_BASE + SURGE_DECAY_PER_TIER * (t - 1);
}

/** §03.6 — dash buffer window 150 ms = 9 sim ticks. */
export const DASH_BUFFER_TICKS = 9;
/** §03.4 — i-frames are exactly 15 ticks (0.25 s) from dash start. */
export const DASH_IFRAME_TICKS = 15;
/** §03.7 — mercy invincibility 0.5 s = 30 ticks. */
export const MERCY_TICKS = 30;
/** §03.7 — player knockback decays at 40 u/s². */
export const PLAYER_KNOCK_DECAY = 40;
/** §4.9 — total movement slow is clamped at 40%. */
export const MAX_SLOW = 0.4;

/**
 * Per-world sim state that has no home in the A.4 entity schema (entities.ts is owned by the
 * integrator and may not be edited by this role). The integrator must include this in A.6
 * snapshots via `serializePlayerRuntime` / `restorePlayerRuntime` for replay-exact resume.
 */
export interface PlayerRuntime {
  /** Position at the start of the current tick — the swept-contact origin (§14.4). */
  prevX: number;
  prevY: number;
  /** Knockback velocity, integrated separately from the movement model (§03.7). */
  knockX: number;
  knockY: number;
  /** Tick of the last surge meter gain; drives the 3 s decay grace (§09.5.1). */
  lastSurgeGainTick: number;
  /** Tick the current dash began (§03.4). */
  dashStartTick: number;
  /** Last non-zero movement heading, used when dashing from idle (§03.4). */
  headingX: number;
  headingY: number;
}

const runtimes = new WeakMap<World, PlayerRuntime>();

export function getPlayerRuntime(w: World): PlayerRuntime {
  let rt = runtimes.get(w);
  if (rt === undefined) {
    rt = {
      prevX: w.player.pos.x,
      prevY: w.player.pos.y,
      knockX: 0,
      knockY: 0,
      lastSurgeGainTick: 0,
      dashStartTick: -1,
      headingX: 1,
      headingY: 0,
    };
    runtimes.set(w, rt);
  }
  return rt;
}

export function serializePlayerRuntime(w: World): PlayerRuntime {
  const rt = getPlayerRuntime(w);
  return {
    prevX: rt.prevX,
    prevY: rt.prevY,
    knockX: rt.knockX,
    knockY: rt.knockY,
    lastSurgeGainTick: rt.lastSurgeGainTick,
    dashStartTick: rt.dashStartTick,
    headingX: rt.headingX,
    headingY: rt.headingY,
  };
}

export function restorePlayerRuntime(w: World, s: PlayerRuntime): void {
  const rt = getPlayerRuntime(w);
  rt.prevX = s.prevX;
  rt.prevY = s.prevY;
  rt.knockX = s.knockX;
  rt.knockY = s.knockY;
  rt.lastSurgeGainTick = s.lastSurgeGainTick;
  rt.dashStartTick = s.dashStartTick;
  rt.headingX = s.headingX;
  rt.headingY = s.headingY;
}

/**
 * Preallocated event payloads — the sim never allocates inside a per-tick path (§14.3).
 * Small rings so two same-type emits inside one tick cannot alias before the flush (A.3).
 */
const DASH_RING = 4;
const SURGE_RING = 8;
const dashPayloads: { phase: 'start' | 'end'; dir: { x: number; y: number } }[] = new Array(DASH_RING);
const surgePayloads: { from: number; to: number; cause: string }[] = new Array(SURGE_RING);
for (let i = 0; i < DASH_RING; i++) dashPayloads[i] = { phase: 'start', dir: { x: 0, y: 0 } };
for (let i = 0; i < SURGE_RING; i++) surgePayloads[i] = { from: 1, to: 1, cause: '' };
let dashCursor = 0;
let surgeCursor = 0;

/** §03.2 — one movement integration step toward `target` at `rate` u/s². */
function approach(current: number, target: number, step: number): number {
  const d = target - current;
  // Tolerance absorbs f64 drift so the ramp lands on the target on the exact acceptance tick.
  if (d > step + 1e-9) return current + step;
  if (d < -step - 1e-9) return current - step;
  return target;
}

/** Top speed after build modifiers (§4.2 cap +40%) and status slow (§4.9). */
export function playerMaxSpeed(w: World): number {
  const p = w.player;
  const base = cfg.get('player.move_speed');
  const mult = p.stats.move_speed_mult > 1.4 ? 1.4 : p.stats.move_speed_mult;
  return base * mult * statusSlowFactor(p);
}

/** §03.4 — dash cooldown after haste, hard floor 1.0 s. */
export function dashCooldownSeconds(w: World): number {
  const base = cfg.get('dash.cooldown_s');
  const floor = cfg.get('dash.cooldown_floor_s');
  const cd = base * w.player.stats.cooldown_mult;
  return cd < floor ? floor : cd;
}

export function dashReady(w: World): boolean {
  const p = w.player;
  return !p.dash.active && w.tick >= p.dash.cooldown_until && !statusStunned(p);
}

/** §03.4 — begins a dash this tick. Direction is locked here and never changes. */
export function startDash(w: World): void {
  const p = w.player;
  const rt = getPlayerRuntime(w);
  let dx = rt.headingX;
  let dy = rt.headingY;
  const m = Math.sqrt(dx * dx + dy * dy);
  if (m < 1e-6) {
    dx = 1;
    dy = 0;
  } else {
    dx /= m;
    dy /= m;
  }
  p.dash.dir.x = dx;
  p.dash.dir.y = dy;
  p.dash.active = true;
  // Fractional end tick keeps the 0.19 s duration exact (11.4 ticks → 6.08 u, §03.4).
  p.dash.end_tick = w.tick + cfg.get('dash.duration_s') * 60;
  p.dash.iframes_until = w.tick + Math.round(cfg.get('dash.iframes_s') * 60);
  p.dash.cooldown_until = w.tick + dashCooldownSeconds(w) * 60;
  if (p.active !== null) p.active.cooldown_until = p.dash.cooldown_until;
  rt.dashStartTick = w.tick;
  w.input.dashBufferedTick = -1;
  emitDash(w, 'start', dx, dy);
}

/** §03.7 — 4 u/s impulse away from the contact normal; never cancels a dash. */
export function applyKnockbackToPlayer(w: World, nx: number, ny: number, impulse: number): void {
  const m = Math.sqrt(nx * nx + ny * ny);
  if (m < 1e-6 || impulse <= 0) return;
  const rt = getPlayerRuntime(w);
  rt.knockX += (nx / m) * impulse;
  rt.knockY += (ny / m) * impulse;
}

export function playerKnockSpeed(w: World): number {
  const rt = getPlayerRuntime(w);
  return Math.sqrt(rt.knockX * rt.knockX + rt.knockY * rt.knockY);
}

// --- Lumensurge (§09.5, spine §2) ---

export function surgeDamageMultiplier(w: World): number {
  return w.player.lumensurge_tier;
}

export function surgeXpMultiplier(w: World): number {
  return w.player.lumensurge_tier;
}

function emitDash(w: World, phase: 'start' | 'end', dx: number, dy: number): void {
  const ev = dashPayloads[dashCursor];
  dashCursor = (dashCursor + 1) % DASH_RING;
  ev.phase = phase;
  ev.dir.x = dx;
  ev.dir.y = dy;
  w.bus.emit('dash', ev);
}

function emitSurge(w: World, from: number, to: number, cause: string): void {
  const ev = surgePayloads[surgeCursor];
  surgeCursor = (surgeCursor + 1) % SURGE_RING;
  ev.from = from;
  ev.to = to;
  ev.cause = cause;
  w.bus.emit('surge_change', ev);
}

/** Recompute the tier from the meter with 5 units of downward hysteresis (§09.5.1). */
function recomputeSurgeTier(w: World, cause: string): void {
  const p = w.player;
  const maxTier = cfg.get('surge.max_tier');
  const from = p.lumensurge_tier;
  let tier = from;
  while (tier < maxTier && tier < SURGE_THRESHOLDS.length && p.surge_progress >= SURGE_THRESHOLDS[tier]) tier++;
  while (tier > 1 && p.surge_progress < SURGE_THRESHOLDS[tier - 1] - SURGE_HYSTERESIS) tier--;
  if (tier !== from) {
    p.lumensurge_tier = tier;
    if (tier > w.counters.peak_surge) w.counters.peak_surge = tier;
    emitSurge(w, from, tier, cause);
  }
}

/** Called by progression (R5) on mote collection. Meter unit = collected mote value. */
export function addSurgeMeter(w: World, amount: number): void {
  if (amount <= 0) return;
  const p = w.player;
  p.surge_progress += amount;
  if (p.surge_progress > SURGE_METER_CAP) p.surge_progress = SURGE_METER_CAP;
  getPlayerRuntime(w).lastSurgeGainTick = w.tick;
  recomputeSurgeTier(w, 'mote');
}

/** §09.5.1 — on any HP loss the meter snaps to the lower bound of (tier − n). */
export function dropSurgeTiers(w: World, tiers: number, cause: string): void {
  const p = w.player;
  if (tiers <= 0 || p.lumensurge_tier <= 1) return;
  const from = p.lumensurge_tier;
  let to = from - tiers;
  if (to < 1) to = 1;
  p.lumensurge_tier = to;
  p.surge_progress = SURGE_THRESHOLDS[to - 1];
  emitSurge(w, from, to, cause);
}

function stepSurgeDecay(w: World): void {
  const p = w.player;
  const rt = getPlayerRuntime(w);
  if (p.surge_progress <= 0) return;
  if ((w.tick - rt.lastSurgeGainTick) / 60 < SURGE_DECAY_GRACE_S) return;
  p.surge_progress -= surgeDecayRate(p.lumensurge_tier) * STEP;
  if (p.surge_progress < 0) p.surge_progress = 0;
  recomputeSurgeTier(w, 'decay');
}

// --- step 2 ---

export function stepPlayer(w: World): void {
  const p = w.player;
  const rt = getPlayerRuntime(w);
  rt.prevX = p.pos.x;
  rt.prevY = p.pos.y;
  if (!p.alive) return;

  // §03.6 — event actions keep a 1-deep buffer counted in sim ticks.
  if (w.input.dashPressed) {
    w.input.dashBufferedTick = w.tick;
    w.input.dashPressed = false;
  }
  if (w.input.dashBufferedTick >= 0 && w.tick - w.input.dashBufferedTick >= DASH_BUFFER_TICKS) {
    w.input.dashBufferedTick = -1;
  }

  // Wish direction, normalized (§03.2 diagonal rule).
  let wx = w.input.moveX;
  let wy = w.input.moveY;
  let wm = Math.sqrt(wx * wx + wy * wy);
  if (wm > 1) {
    wx /= wm;
    wy /= wm;
    wm = 1;
  }
  if (!p.dash.active && wm > 1e-4) {
    rt.headingX = wx / wm;
    rt.headingY = wy / wm;
  }

  // §03.4 — a buffered dash fires on the first legal tick.
  if (w.input.dashBufferedTick >= 0 && dashReady(w)) startDash(w);

  // Dash expiry: the displacement window closes, the i-frame tail keeps running.
  if (p.dash.active && p.dash.end_tick - w.tick <= 0) {
    p.dash.active = false;
    emitDash(w, 'end', p.dash.dir.x, p.dash.dir.y);
    const exit = playerMaxSpeed(w);
    p.vel.x = p.dash.dir.x * exit;
    p.vel.y = p.dash.dir.y * exit;
  }

  const vmax = playerMaxSpeed(w);

  if (p.dash.active) {
    // Sub-tick clipped integration: 0.19 s = 11.4 ticks → exactly 6.08 u (§03.4 acceptance).
    let frac = p.dash.end_tick - w.tick;
    if (frac > 1) frac = 1;
    const speed = cfg.get('dash.speed');
    p.pos.x += p.dash.dir.x * speed * STEP * frac;
    p.pos.y += p.dash.dir.y * speed * STEP * frac;
    p.vel.x = p.dash.dir.x * speed;
    p.vel.y = p.dash.dir.y * speed;
  } else if (statusStunned(p)) {
    p.vel.x = 0;
    p.vel.y = 0;
  } else {
    // §03.2 — accelerate / brake / reverse-decel toward the wish velocity.
    const tx = wx * vmax;
    const ty = wy * vmax;
    let rate: number;
    if (wm <= 1e-4) {
      rate = cfg.get('player.brake');
    } else if (wx * p.vel.x + wy * p.vel.y < 0) {
      rate = cfg.get('player.reverse_decel');
    } else {
      rate = cfg.get('player.accel');
    }
    const stepMag = rate * STEP;
    p.vel.x = approach(p.vel.x, tx, stepMag);
    p.vel.y = approach(p.vel.y, ty, stepMag);
    const sp = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y);
    if (sp > vmax && sp > 1e-9) {
      p.vel.x = (p.vel.x / sp) * vmax;
      p.vel.y = (p.vel.y / sp) * vmax;
    }
    p.pos.x += p.vel.x * STEP;
    p.pos.y += p.vel.y * STEP;
    // §03.7 — knockback rides on top of the movement model and decays at 40 u/s².
    p.pos.x += rt.knockX * STEP;
    p.pos.y += rt.knockY * STEP;
  }

  const km = Math.sqrt(rt.knockX * rt.knockX + rt.knockY * rt.knockY);
  if (km > 0) {
    const decayed = km - PLAYER_KNOCK_DECAY * STEP;
    if (decayed <= 1e-9) {
      rt.knockX = 0;
      rt.knockY = 0;
    } else {
      rt.knockX = (rt.knockX / km) * decayed;
      rt.knockY = (rt.knockY / km) * decayed;
    }
  }

  wrapPlayerPosition(w);

  // Regeneration (§06 passives) — fixed sim-tick rate, never haste-affected (§4.6).
  if (p.stats.regen_per_s > 0 && p.hp < p.max_hp) {
    p.hp += p.stats.regen_per_s * STEP;
    if (p.hp > p.max_hp) p.hp = p.max_hp;
  }

  stepSurgeDecay(w);
}

/**
 * AMD-24 — the arena has no walls. Position is canonicalised into `[-100, 100)`; velocity and
 * knockback are untouched, so movement and dash carry straight through the seam.
 */
export function wrapPlayerPosition(w: World): void {
  wrapPos(w.player.pos);
}
